import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';
import { CorporateBankAccountsManager } from 'back-office/features/plateformes-view/detail/CorporateBankAccountsManager';
import { PersonneMoraleProfileHeader } from 'back-office/shared/components/personnes-morales/PersonneMoraleProfileHeader';
import { PersonneMoraleProfileTabs } from 'back-office/shared/components/personnes-morales/PersonneMoraleProfileTabs';
import { PersonneMoraleInfoCard } from 'back-office/shared/components/personnes-morales/PersonneMoraleInfoCard';
import { profileFromPersonneMorale } from 'back-office/shared/components/personnes-morales/personneMoraleProfile';
import { getPersonneMoraleMembers } from 'features/access/api/personneMoraleAccessApi';
import { getPersonneMoralePlateformes } from 'back-office/features/personne-morale-plateformes/api/personneMoralePlateformesApi';

import {
  PersonneMoraleListItemDto,
  getPersonneMorale
} from './api/personnesMoralesApi';
import { PersonneMoraleAssociatedPlateformes } from './sections/PersonneMoraleAssociatedPlateformes';

/**
 * Full details page of a personne morale, on the shared PM profile layout
 * (hero + Identity / Collaborators tabs) — same render as the Gouvernance tab
 * of a plateforme and the intermediary quick view, plus a "Plateformes" tab
 * specific to this page.
 */
export const PersonneMoraleDetails: React.FC = () => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  const { personneMoraleId } = useParams<{ personneMoraleId: string }>();
  const navigate = useNavigate();

  const [pm, setPm] = useState<PersonneMoraleListItemDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [membersCount, setMembersCount] = useState<number | null>(null);
  const [plateformesCount, setPlateformesCount] = useState<number | null>(null);

  useEffect(() => {
    if (!personneMoraleId) return;
    let cancelled = false;
    setLoading(true);
    getPersonneMorale(personneMoraleId)
      .then(data => {
        if (!cancelled) setPm(data);
      })
      .catch(() => {
        if (!cancelled) {
          toasterService.showToast({
            message: t('profile.loadError'),
            displayType: DisplayTypeEnum.Toaster,
            color: GenericColors.Red
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [personneMoraleId, t]);

  // Hero counters — best effort (the tabs, unmounted until opened, refresh
  // them afterwards through their callbacks).
  useEffect(() => {
    if (!personneMoraleId) return;
    getPersonneMoraleMembers(personneMoraleId)
      .then(data => setMembersCount(data.collaborators.length))
      .catch(() => setMembersCount(null));
    getPersonneMoralePlateformes(personneMoraleId)
      .then(data => setPlateformesCount(data.plateformes.length))
      .catch(() => setPlateformesCount(null));
  }, [personneMoraleId]);

  if (!personneMoraleId) {
    return <div className="p-6">{t('profile.missingId')}</div>;
  }

  if (loading) {
    return <GenericLoading loading isFullPage />;
  }

  if (!pm) {
    return <div className="p-6">{t('profile.notFound')}</div>;
  }

  const profile = profileFromPersonneMorale(pm);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate('..')}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-primary"
      >
        <ArrowLeft size={16} />
        {t('profile.backToList')}
      </button>

      {/* Hero + tabs card: the hero is the card header (edge-to-edge via the
          variant wrapper, without touching the shared component). */}
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="[&_.pm-profile-hero]:rounded-none">
          <PersonneMoraleProfileHeader
            profile={profile}
            overline={t('profile.legalEntity')}
            badges={
              pm.isOriasSociety ? (
                <span
                  title={
                    pm.lastControlOrias
                      ? t('profile.oriasLastControl', {
                          date: new Date(
                            pm.lastControlOrias
                          ).toLocaleDateString()
                        })
                      : undefined
                  }
                  className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    pm.isValidOrias
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {pm.isValidOrias
                    ? t('profile.oriasValid')
                    : t('profile.oriasInvalid')}
                </span>
              ) : undefined
            }
            stats={[
              { label: t('profile.stats.orias'), value: profile.orias },
              {
                label: sirenOrSiretLabel(profile.siren ?? ''),
                value: profile.siren
              },
              {
                label: t('profile.stats.activeMembers'),
                value: membersCount ?? undefined
              },
              {
                label: t('profile.stats.plateformes'),
                value: plateformesCount ?? undefined
              }
            ]}
          />
        </div>

        <div className="px-6 pb-6 pt-4 shadow-inner">
          <PersonneMoraleProfileTabs
            personneMoraleId={personneMoraleId}
            membersCount={membersCount}
            onMembersCountChange={setMembersCount}
            leftColumn={
              <>
                <PersonneMoraleInfoCard profile={profile} variant="identity" />
                <CorporateBankAccountsManager personId={personneMoraleId} />
              </>
            }
            rightColumn={
              <PersonneMoraleInfoCard profile={profile} variant="contact" />
            }
            plateformesCount={plateformesCount}
            plateformesTab={
              <PersonneMoraleAssociatedPlateformes
                personneMoraleId={personneMoraleId}
                onCountChange={setPlateformesCount}
              />
            }
          />
        </div>
      </section>
    </div>
  );
};

export default PersonneMoraleDetails;
